/**
 * @file Forwards the `LearnerEvents` bus's engagement-lifecycle
 *       transitions (`suspend` / `resume` / `expired`) onto the shared
 *       Umami `track()` path as the umbrella's locked
 *       `engagement_paused` / `engagement_resumed` / `engagement_expired`
 *       events.
 * @module @labcraft/lab/ui/engagement-analytics
 *
 * The bus already emits these transitions at the UI commit boundary
 * (see {@link module:@labcraft/lab/ui/learner-events} — `TerminalPane`
 * re-emits from its lifecycle-frame effect for Playwright specs). This
 * module taps the *same* emissions for product analytics so there is a
 * single source of truth for "the machine paused/resumed/was reaped":
 * the test bridge and Umami read the identical bus events rather than a
 * parallel `track()` call scattered across the terminal-pane effect.
 *
 * The bus event names are the backend-mirrored `suspend` / `resume` /
 * `expired`; the Umami event names are the umbrella's locked
 * `engagement_*` catalog names. The mapping is fixed and one-way:
 *
 *   bus `suspend`  → `engagement_paused`   (voluntary/idle pause)
 *   bus `resume`   → `engagement_resumed`  (resume-after-pause)
 *   bus `expired`  → `engagement_expired`  (engagement TTL reap)
 *
 * Idempotency: `main.tsx` calls {@link forwardEngagementAnalytics} once
 * at boot, but StrictMode double-invocation and any re-install of the
 * bus must not double-register — a second registration would fire each
 * Umami event twice per transition. The forwarder guards on a
 * bus-mounted flag so re-invocation is a no-op, mirroring
 * `installLearnerEvents`'s reuse-the-existing-instance idiom.
 */

import { track } from '@labcraft/ui/hooks'
import type { LearnerEvents } from './learner_events.ts'

/**
 * Property key the forwarder stamps on the bus once it has wired its
 * listeners, so a second {@link forwardEngagementAnalytics} call (React
 * StrictMode double-mount, a dev re-render, a bus re-install) is a
 * no-op instead of a duplicate registration. A string key,
 * double-underscore-named to signal "internal plumbing, not product
 * state" — same convention as the bus's own `__learnerEvents__` key.
 */
const FORWARDED_FLAG = '__engagementAnalyticsForwarded__' as const

/**
 * Wire the engagement-lifecycle bus transitions to Umami `track()`.
 * Registers one `suspend` / `resume` / `expired` listener each,
 * mapping to the locked `engagement_paused` / `engagement_resumed` /
 * `engagement_expired` events. No-op when analytics is dark — `track`
 * itself no-ops until the tracker script has loaded.
 *
 * Idempotent: the second and later calls return immediately without
 * registering a duplicate listener, so a transition never fires its
 * Umami event more than once per occurrence.
 *
 * @param bus - The window-mounted `LearnerEvents` bus (from
 *   `installLearnerEvents()`), or `null` outside the browser — in which
 *   case forwarding is skipped.
 */
export function forwardEngagementAnalytics(bus: LearnerEvents | null): void {
  if (!bus) return

  const guarded = bus as unknown as Record<string, unknown>
  if (guarded[FORWARDED_FLAG]) return
  guarded[FORWARDED_FLAG] = true

  bus
    .on('suspend', () => track('engagement_paused'))
    .on('resume', () => track('engagement_resumed'))
    .on('expired', () => track('engagement_expired'))
}
